import { computed, ref } from 'vue'

import { ACTIVITY_REQUEST_SAVED_VIEWS_PREFIX } from '@/constants/activity-requests.constants'

import {
  activityRequestFiltersFromUrl,
  activityRequestFiltersToUrl
} from './useActivityRequestFilters'
import type { ActivityRequestFilters, ActivityRequestUrlQuery } from './useActivityRequestFilters'
import {
  currentUserStorageId,
  readStorageJson,
  writeStorageJson
} from '../utils/activity-request-storage'

/**
 * Visões salvas da aba Solicitações (Q14): `localStorage`, por usuário. Guardam a query no
 * FORMATO DA URL — assim uma visão antiga passa pelo mesmo parser tolerante de um link
 * editado à mão, e um filtro que deixou de existir cai no padrão em vez de ir cru ao backend.
 */
export interface ActivityRequestSavedView {
  id: string
  name: string
  query: ActivityRequestUrlQuery
  isDefault: boolean
}

export const SAVED_VIEW_NAME_MAX_LENGTH = 60

const storageKey = (): string => `${ACTIVITY_REQUEST_SAVED_VIEWS_PREFIX}${currentUserStorageId()}`

const isQueryRecord = (value: unknown): value is ActivityRequestUrlQuery =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (item) =>
      typeof item === 'string' ||
      (Array.isArray(item) && item.every((part) => typeof part === 'string'))
  )

function readViews(): ActivityRequestSavedView[] {
  const raw = readStorageJson(storageKey())
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry): ActivityRequestSavedView[] => {
    if (typeof entry !== 'object' || entry === null) return []
    const { id, name, query, isDefault } = entry as Record<string, unknown>
    if (typeof id !== 'string' || typeof name !== 'string' || !isQueryRecord(query)) return []
    return [{ id, name, query, isDefault: isDefault === true }]
  })
}

const newId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export function useActivityRequestSavedViews() {
  const views = ref<ActivityRequestSavedView[]>(readViews())

  const defaultView = computed(() => views.value.find((view) => view.isDefault) ?? null)

  const persist = (): void => writeStorageJson(storageKey(), views.value)

  function saveView(
    name: string,
    filters: ActivityRequestFilters,
    isDefault: boolean
  ): ActivityRequestSavedView | null {
    const trimmed = name.trim().slice(0, SAVED_VIEW_NAME_MAX_LENGTH)
    if (!trimmed) return null

    // A busca por texto nunca é salva (M1-B, balão 4); a página também não.
    const query = activityRequestFiltersToUrl({ ...filters, search: '' }, 1)
    const existing = views.value.find((view) => view.name === trimmed)
    const saved: ActivityRequestSavedView = {
      id: existing?.id ?? newId(),
      name: trimmed,
      query,
      isDefault
    }

    const others = views.value
      .filter((view) => view.id !== saved.id)
      .map((view) => (isDefault ? { ...view, isDefault: false } : view))
    views.value = [...others, saved]
    persist()
    return saved
  }

  function removeView(id: string): void {
    views.value = views.value.filter((view) => view.id !== id)
    persist()
  }

  function setDefault(id: string | null): void {
    views.value = views.value.map((view) => ({ ...view, isDefault: view.id === id }))
    persist()
  }

  const filtersOf = (view: ActivityRequestSavedView): ActivityRequestFilters =>
    activityRequestFiltersFromUrl(view.query).filters

  return { views, defaultView, saveView, removeView, setDefault, filtersOf }
}
